import { type VariantProps, cva } from 'class-variance-authority';
import { useState, type JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import type { CommonProps } from '../../types';
import { ChevronLeftLightIcon, ChevronRightLightIcon } from '../../';
import {
    MappedContentTile,
    MappedContentTileImage,
} from '@beat-venues/contentful';
import { EventContentCard } from '../../molecules/event-content-card';
import { useTranslation } from 'react-i18next';

export interface CarouselProps
    extends CommonProps,
        VariantProps<typeof CarouselStyles> {
    content: MappedContentTile[];
}

export const CarouselStyles = cva('relative w-full max-w-4xl mx-auto');

export const Carousel = ({
    className,
    content,
    testId = 'carousel',
    ...props
}: CarouselProps): JSX.Element => {
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = useState<number>(0);

    const previousSlide = () => {
        const newIndex =
            currentIndex === 0 ? content.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);
    };

    const nextSlide = () => {
        const newIndex =
            currentIndex === content.length - 1 ? 0 : currentIndex + 1;
        setCurrentIndex(newIndex);
    };

    const goToSlide = (index: number) => {
        setCurrentIndex(index);
    };

    return (
        <div
            className={twMerge(CarouselStyles({ className }))}
            {...props}
            data-testid={testId}
        >
            {/* Main slide */}
            <div className="relative overflow-hidden rounded-lg">
                {content?.length > 0 && (
                    <EventContentCard
                        key={`${currentIndex}-${content[currentIndex]?.title}`}
                        image={{
                            alt:
                                typeof content[currentIndex]?.image !== 'string'
                                    ? (
                                          content[currentIndex]
                                              ?.image as MappedContentTileImage
                                      ).altText
                                    : (content[currentIndex]?.image as string),
                            url:
                                typeof content[currentIndex]?.image !== 'string'
                                    ? (
                                          content[currentIndex]
                                              ?.image as MappedContentTileImage
                                      ).image.file?.url || ''
                                    : (content[currentIndex]?.image as string),
                            aspectRatio: 'BANNER',
                        }}
                        slug={content[currentIndex]?.cta.externalUrl}
                        title={content[currentIndex]?.title}
                        buttonLabel={content[currentIndex]?.cta.label}
                    />
                )}
                {/* Navigation arrows */}
                <button
                    onClick={previousSlide}
                    className="absolute left-4 top-[45%] -translate-y-1/2 rounded-full bg-gray-900/50 px-1.5 pb-0.5 transition-colors hover:bg-gray-900/70"
                >
                    <span className="sr-only">{t('previousSlide')}</span>
                    <ChevronLeftLightIcon size="sm" />
                </button>
                <button
                    onClick={nextSlide}
                    className="absolute right-4 top-[45%] -translate-y-1/2 rounded-full bg-gray-900/40 px-1.5 pb-0.5 transition-colors hover:bg-gray-900/60"
                >
                    <span className="sr-only">{t('nextSlide')}</span>
                    <ChevronRightLightIcon size="sm" />
                </button>
            </div>

            {/* Dots/slider indicators */}
            <div className="absolute bottom-1/3 left-1/2 flex -translate-x-1/2 space-x-2">
                {content &&
                    content.length > 0 &&
                    content.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`h-3 w-3 rounded-full transition-colors ${
                                index === currentIndex
                                    ? 'bg-white'
                                    : 'bg-white/50'
                            }`}
                        >
                            <span className="sr-only">
                                {t('dotIndicator', {
                                    slideIndex: index + 1,
                                    slidesLength: content.length,
                                })}
                            </span>
                        </button>
                    ))}
            </div>
        </div>
    );
};
